/**
 * Module for managing a Table of Contents (TOC) and updating active states during user interactions.
 *
 * Handles click and scroll behavior for TOC links, highlights the current section,
 * and manages accordion and visual overlays based on background media.
 *
 * @param {HTMLElement} module - The root module element containing the TOC.
 * @exports
 * @author Dreamers of Day
 */
export default function (module) {
    // Tracks the currently active section element (not used further, kept for potential expansion)
    let activeSection = null;

    // Stores the ID of the currently active section
    let activeSectionId = '';

    // Flag to prevent scroll-based TOC updates while user-initiated scroll is in progress
    let preventScrollHandling = false;

    // Stores the requestAnimationFrame ID used for debouncing scroll events
    let scrollDebounceFrame;

    // Query selectors used to locate relevant DOM elements within the module
    const selectors = {
        tocList: '.js-m-toc-list',
        tocLink: '.js-m-toc-link',
        tocProgressbar: '.js-m-toc-progressbar',
        navbar: '.js-m-toc-navbar',
        backgroundImages: '.o-block--with-toc-over-media .a-img',
        backgroundVideo: '.o-block--with-toc-over-media .a-video',
    };

    // CSS classes added to TOC links when active
    const activeClasses = {
        link: ['bg-[#FFEFA6]'],
    };

    // CSS classes applied when TOC links are inactive
    const inactiveClasses = {
        link: ['text-brand-color-10'],
    };

    // Reference to TOC list container
    const tocList = module.querySelector(selectors.tocList);

    // Reference to the navbar container
    const navbar = module.querySelector(selectors.navbar);

    // NodeList of background images and videos
    const images = document.querySelectorAll(selectors.backgroundImages);
    const videos = document.querySelectorAll(selectors.backgroundVideo);

    // Add event listeners to TOC list and window scroll
    if (tocList) {
        tocList.addEventListener('click', handleTocLinkClick);
        window.addEventListener('scroll', handleWindowScroll);
    }

    /**
     * Handles click events on TOC links.
     * Smoothly scrolls to the target section and applies active styling.
     *
     * @param {MouseEvent} event - Click event on a TOC link.
     * @returns {void}
     */
    function handleTocLinkClick(event) {
        // Find the section the clicked link points to
        const targetSection = getTargetSectionFromLink(event.target);

        if (!targetSection) {
            return;
        }

        // Scroll to calculated position for the target section
        window.scrollTo({
            top: calculateScrollToPosition(targetSection),
            behavior: 'smooth',
        });

        // Prevent scroll event handling during smooth scroll animation
        preventScrollHandling = true;

        // Update classes for all TOC links
        Array.from(getAllTocLinks()).forEach((link) => {
            const section = getTargetSectionFromLink(link);
            if (section === targetSection) {
                applyClasses(link, activeClasses.link, inactiveClasses.link);
            } else {
                applyClasses(link, inactiveClasses.link, activeClasses.link);
            }
        });

        // Reset scroll prevention after a short timeout
        setTimeout(() => {
            preventScrollHandling = false;
        }, 1000);
    }

    /**
     * Handles scroll events by deferring update to next animation frame.
     *
     * @returns {void}
     */
    function handleWindowScroll() {
        if (scrollDebounceFrame || preventScrollHandling) {
            return;
        }
        scrollDebounceFrame = requestAnimationFrame(updateTocActiveState);
    }

    /**
     * Updates TOC links based on which section is visible in the viewport.
     *
     * @returns {void}
     */
    function updateTocActiveState() {
        const links = Array.from(getAllTocLinks());
        let firstVisible = null;

        // Iterate over all TOC links to find the first visible section
        for (const link of links) {
            const section = getTargetSectionFromLink(link);
            if (section && isSectionInView(section)) {
                firstVisible = { id: link.dataset.targetId, link };
                break; // Only the first visible section is highlighted
            }
        }

        // Apply active/inactive styles based on visibility check
        links.forEach((link) => {
            const id = link.dataset.targetId;
            if (firstVisible && id === firstVisible.id) {
                applyClasses(link, activeClasses.link, inactiveClasses.link);
                activeSectionId = id;
            } else {
                applyClasses(link, inactiveClasses.link, activeClasses.link);
            }
        });

        scrollDebounceFrame = null;
    }

    /**
     * Returns the section element associated with a TOC link element.
     *
     * @param {HTMLElement} element - A TOC link or its child element.
     * @returns {HTMLElement | null} The matched section element.
     */
    function getTargetSectionFromLink(element) {
        const link = element.closest(selectors.tocLink);
        if (!link) {
            return null;
        }
        const sectionId = link.dataset.targetId;
        const anchor = document.getElementById(sectionId);
        return anchor ? anchor.closest('section.o-block') : null;
    }

    /**
     * Computes scroll position for a section including header offset.
     *
     * @param {HTMLElement} section - Target section to scroll to.
     * @returns {number} Scroll Y position.
     */
    function calculateScrollToPosition(section) {
        return getSectionOffsetFromDocument(section) - getScrollOffset();
    }

    /**
     * Calculates vertical offset from top of the document to section.
     *
     * @param {HTMLElement} section - Section element.
     * @returns {number} Absolute Y offset.
     */
    function getSectionOffsetFromDocument(section) {
        return (
            section.getBoundingClientRect().top +
            (window.scrollY || document.documentElement.scrollTop)
        );
    }

    /**
     * Checks whether a section is in view.
     *
     * @param {HTMLElement} section - Section to test.
     * @returns {boolean} True if in view.
     */
    function isSectionInView(section) {
        if (!section) {
            return false;
        }

        const start = calculateScrollToPosition(section);
        const end = start + section.offsetHeight;
        const scrollY = window.scrollY || document.documentElement.scrollTop;

        return scrollY + 1 > start && scrollY - 1 < end;
    }

    /**
     * Returns all TOC link elements inside the module.
     *
     * @returns {NodeListOf<HTMLElement>} Collection of links.
     */
    function getAllTocLinks() {
        return module.querySelectorAll(selectors.tocLink);
    }

    /**
     * Returns scroll offset from sticky header.
     *
     * @returns {number} Offset in pixels.
     */
    function getScrollOffset() {
        const header = document.querySelector('.js-header');
        return header ? header.offsetHeight : 150;
    }

    /**
     * Applies and removes classes from a DOM element.
     *
     * @param {HTMLElement} element - Element to modify.
     * @param {string[]} addClasses - Classes to add.
     * @param {string[]} removeClasses - Classes to remove.
     */
    function applyClasses(element, addClasses, removeClasses) {
        element.classList.remove(...removeClasses);
        element.classList.add(...addClasses);
    }

    /**
     * Initializes accordion behavior for collapsible TOC sections.
     *
     * @returns {void}
     */
    function initAccordion() {
        const trigger = module.querySelector('.js-m-toc-accordion');
        const list = module.querySelector('.js-m-toc-accordion-list');

        if (!trigger || !list) {
            return;
        }

        list.style.overflow = 'hidden';
        list.style.transition = 'max-height 0.3s ease';
        list.classList.add('is-open');
        list.style.maxHeight = list.scrollHeight + 'px';
        trigger.classList.add('is-active');

        trigger.addEventListener('click', () => {
            const isOpen = list.classList.contains('is-open');
            if (isOpen) {
                list.classList.remove('is-open');
                list.style.maxHeight = '0';
                trigger.classList.remove('is-active');
            } else {
                list.classList.add('is-open');
                list.style.maxHeight = list.scrollHeight + 'px';
                trigger.classList.add('is-active');
            }
        });

        window.addEventListener('resize', () => {
            if (list.classList.contains('is-open')) {
                list.style.maxHeight = list.scrollHeight + 'px';
            }
        });
    }

    /**
     * Observes TOC navbar overlap with media elements and applies visual classes.
     *
     * @returns {void}
     */
    function initNavbarObserver() {
        if (!navbar || (images.length === 0 && videos.length === 0)) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                let isOverMedia = entries.some((entry) => entry.isIntersecting);
                navbar.classList.toggle('m-toc--over-media', isOverMedia);
            },
            { root: null, threshold: 0.5 },
        );

        images.forEach((img) => observer.observe(img));
        videos.forEach((vid) => observer.observe(vid));
    }

    // Initialize accordion and observer on load
    initAccordion();
    initNavbarObserver();
}
